import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import TeamCard from './TeamCard';
import { Team } from '../services/api';

interface TeamListProps {
  teams: Team[];
  loading?: boolean;
  error?: string;
  onEdit?: (team: Team) => void;
  onDelete?: (teamId: string) => void;
  showActions?: boolean;
  allowFiltering?: boolean;
}

const TeamList: React.FC<TeamListProps> = ({
  teams,
  loading = false,
  error,
  onEdit,
  onDelete,
  showActions = false,
  allowFiltering = true,
}) => {
  const [filterType, setFilterType] = useState<string>('all');

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterType(event.target.value);
  };

  const filteredTeams = filterType === 'all'
    ? teams
    : teams.filter(team => team.doubles_type === filterType);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {allowFiltering && teams.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={filterType}
              label="Filter by Type"
              onChange={handleFilterChange}
            >
              <MenuItem value="all">All Teams</MenuItem>
              <MenuItem value="men">Men's Doubles</MenuItem>
              <MenuItem value="women">Women's Doubles</MenuItem>
              <MenuItem value="mixed">Mixed Doubles</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {filteredTeams.length === 0 ? (
        <Alert severity="info">
          {filterType === 'all'
            ? 'No teams registered yet'
            : `No ${filterType} doubles teams registered yet`}
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredTeams.length} {filteredTeams.length === 1 ? 'team' : 'teams'}
          </Typography>
          <Grid container spacing={3}>
            {filteredTeams.map(team => (
              <Grid item xs={12} sm={6} md={4} key={team.id}>
                <TeamCard
                  team={team}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  showActions={showActions}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default TeamList;
